import { TableFooter } from '@fukuri-admins/features/Table';
import { FileDownloadIcon } from '@fukuri-admins/ui/common';
import { useToast } from '@fukuri-admins/ui/common/Toast/hooks';
import { CircularProgress } from '@mui/material';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { getCompanyDeposits, getCompanyDepositsCSV } from '../../../apis/companyDeposits/index';
import { useAuth } from '../../../components/auth/AuthProvider.tsx';
import { ContentsHeader } from '../../../components/common/ContentsHeader';
import { CompanyDepositMobileCardList } from '../../../components/companyDeposit/MobileCardList/index';
import { CompanyDepositTableContent } from '../../../components/companyDeposit/TableContent/index.tsx';
import { useIsMobile } from '../../../hooks/useIsMobile';
import { CompanyDepositTableData } from '../../../types/companyDeposit/CompanyDepositTableData.ts';
import { ActionTableColumnsType } from '../../../types/table/ActionTableColumnsType';
import {
  ActionTableSortParamsType,
  defaultActionTableSortParams,
} from '../../../types/table/ActionTableSortParamsType';
import { downloadFile } from '../../../utils/download';
import { handleSorting } from '../../../utils/sortings';
import styles from './styles.module.scss';

export const IndexCompanyDeposit: React.FC = () => {
  const isMobile = useIsMobile();
  const [filteredCompanyDeposits, setFilteredCompanyDeposits] = useState<CompanyDepositTableData[]>(
    []
  );
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [totalPage, setTotalPage] = useState<number>(0);
  const [totalCount, setTotalCount] = useState<number>(0);
  const { signOut } = useAuth();
  const [filterParams] = useSearchParams();
  const [sortParams, setSortParams] = useState<ActionTableSortParamsType>({
    ...defaultActionTableSortParams,
    column: 'no',
  });

  const { showToast } = useToast();

  const companyDepositTableColumns: ActionTableColumnsType[] = [
    {
      label: '入金金額',
      key: 'amount',
      sortable: true,
      filterable: true,
    },
    {
      label: '集計月',
      key: 'target_month',
      sortable: true,
      filterable: true,
    },
    {
      label: '入金ステータス',
      key: 'deposit_status',
      sortable: true,
      filterable: true,
    },
  ];

  const handleOutputCsv = async () => {
    const { data } = await getCompanyDepositsCSV(signOut);
    if (data instanceof Blob) {
      downloadFile(data, 'service_usages.csv');
    }
  };

  const getCompanyDepositsData = async (page?: number) => {
    try {
      const pageNumber = page ? page : filterParams.get('page');
      const { data, headers } = await getCompanyDeposits(signOut, {
        ...(pageNumber && { page: pageNumber.toString() }),
      });

      updatePaginationInfo(headers);
      setFilteredCompanyDeposits(data.company_deposits);
    } catch {
      showToast({ message: 'データの取得に失敗しました。', status: 'danger' });
    } finally {
      setIsLoading(false);
    }
  };

  const handlePageChange = (page: number) => {
    setIsLoading(true);
    getCompanyDepositsData(page);
  };

  const updatePaginationInfo = (headers: unknown) => {
    setTotalPage(Number(headers['total-pages']));
    setTotalCount(Number(headers['total-count']));
  };

  useEffect(() => {
    getCompanyDepositsData();
  }, []);

  return (
    <div className={styles.Root} data-testid='clients-page'>
      <ContentsHeader
        title='入金一覧'
        description={isMobile ? '' : '入金の一覧を表示しています。'}
        actions={
          !isMobile && (
            <div className={styles.menuContainer}>
              <button className={styles.outputCsv} onClick={handleOutputCsv}>
                <FileDownloadIcon />
                CSV出力
              </button>
            </div>
          )
        }
      />
      <div className={styles.tableContainer}>
        {isLoading ? (
          <div className={styles.loading}>
            <CircularProgress size='1rem' color='inherit' />
          </div>
        ) : (
          <>
            {isMobile ? (
              <CompanyDepositMobileCardList rows={filteredCompanyDeposits} />
            ) : (
              <CompanyDepositTableContent
                rows={filteredCompanyDeposits}
                columns={companyDepositTableColumns}
                sortParams={sortParams}
                headerSort={column =>
                  handleSorting(
                    column,
                    sortParams,
                    filteredCompanyDeposits,
                    setSortParams,
                    setFilteredCompanyDeposits
                  )
                }
              />
            )}
            <TableFooter
              onPageChange={handlePageChange}
              totalCount={totalCount}
              totalPage={totalPage}
              itemCount={filteredCompanyDeposits.length}
            />
          </>
        )}
      </div>
    </div>
  );
};
